import React from 'react';
import UserAvatar from '../../ui/UserAvatar';
import { MailIcon, PhoneIcon } from '../../ui/ContactIcons';
import {
  DOTBOARD_STATUS_LABELS,
  formatDotboardAge,
  formatDotboardSince,
  type DotboardPerson,
} from './dotboardModel';

function PinIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" role="img" aria-label="Set automatically">
      <path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

function WarnIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />
    </svg>
  );
}

interface DotboardPersonRowProps {
  person: DotboardPerson;
  isYou: boolean;
  expanded: boolean;
  now: number;
  chatOpening: boolean;
  onToggle: (personId: string) => void;
  onChat: (profileId: string) => void;
}

/**
 * One person on the dotboard. Clicking the row opens contact details inline;
 * clicking again closes them (there is no separate Close button).
 */
function DotboardPersonRow({ person, isYou, expanded, now, chatOpening, onToggle, onChat }: DotboardPersonRowProps) {
  const detailsId = React.useId();
  const profileId = person.profileId;
  const firstName = person.name.split(/\s+/)[0] || person.name;
  const callPhone = person.phones.find((phone) => phone.kind === 'mobile') || person.phones[0] || null;
  const callLabel = callPhone
    ? `Call ${firstName}${callPhone.kind === 'mobile' ? ' on mobile' : callPhone.kind === 'office' ? ' at the office' : ''}`
    : `Call ${firstName}: no phone number on file`;

  return (
    <li className="dotboard-person" data-expanded={expanded ? 'true' : undefined}>
      <button
        type="button"
        className="dotboard-person__row"
        aria-expanded={expanded}
        aria-controls={expanded ? detailsId : undefined}
        onClick={() => onToggle(person.id)}
      >
        <span className="dotboard-person__avatar">
          <UserAvatar name={person.name} src={person.photo} fallbackSrc={person.portalPhoto} size={40} />
          <span className="dotboard-dot dotboard-person__dot" data-status={person.status} aria-hidden="true" />
        </span>
        <span className="dotboard-person__main">
          <span className="dotboard-person__name-line">
            <span className="dotboard-person__name">{person.name}</span>
            {isYou ? <span className="dotboard-person__you">(you)</span> : null}
            <span className="sr-only">, {DOTBOARD_STATUS_LABELS[person.status]}</span>
          </span>
          <span className="dotboard-person__comment" data-empty={person.comment ? undefined : 'true'}>
            {person.comment && person.automatic ? <PinIcon /> : null}
            <span>{person.comment || 'No comment'}</span>
          </span>
        </span>
        <span className="dotboard-person__when" data-stale={person.stale ? 'true' : undefined}>
          <span>{formatDotboardSince(person.updatedAt, now)}</span>
          {person.stale ? (
            <span className="dotboard-person__stale-tag">
              <WarnIcon />
              Not updated in {formatDotboardAge(person.updatedAt, now)}
            </span>
          ) : null}
        </span>
      </button>

      {expanded ? (
        <div id={detailsId} className="dotboard-person__details">
          <div className="dotboard-person__details-top">
            <span className="dotboard-person__job" data-empty={person.jobTitle ? undefined : 'true'}>
              {person.jobTitle || 'No job title on file'}
            </span>
            <span className="dotboard-person__actions">
              {callPhone ? (
                <a className="dotboard-icon-btn ui-tooltip" href={callPhone.href} aria-label={callLabel} data-tooltip={callLabel}>
                  <PhoneIcon />
                </a>
              ) : (
                // aria-disabled (not disabled) so the hover message still shows.
                <button
                  type="button"
                  className="dotboard-icon-btn ui-tooltip"
                  aria-disabled="true"
                  aria-label={callLabel}
                  data-tooltip="No phone number on file"
                >
                  <PhoneIcon />
                </button>
              )}
              {person.email ? (
                <a className="dotboard-icon-btn ui-tooltip" href={`mailto:${person.email}`} aria-label={`Email ${firstName}`} data-tooltip={`Email ${firstName}`}>
                  <MailIcon />
                </a>
              ) : null}
              {profileId && !isYou ? (
                <button
                  type="button"
                  className="dotboard-icon-btn ui-tooltip"
                  aria-label={`Chat with ${firstName}`}
                  data-tooltip={`Chat with ${firstName}`}
                  disabled={chatOpening}
                  onClick={() => onChat(profileId)}
                >
                  <ChatIcon />
                </button>
              ) : null}
            </span>
          </div>
          <dl className="dotboard-person__facts">
            {person.phones.length ? person.phones.map((phone) => (
              <div key={`${phone.kind}-${phone.number}`}>
                <dt>{phone.label}</dt>
                <dd><a href={phone.href}>{phone.number}</a></dd>
              </div>
            )) : (
              <div>
                <dt>Phone</dt>
                <dd data-empty="true">Not listed</dd>
              </div>
            )}
            {person.email ? (
              <div className="dotboard-person__fact--wide">
                <dt>Email</dt>
                <dd><a href={`mailto:${person.email}`}>{person.email}</a></dd>
              </div>
            ) : null}
          </dl>
          {person.stale ? (
            <p className="dotboard-person__stale-note">
              <WarnIcon />
              No status change in over 2 weeks, so this may be out of date.
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export default React.memo(DotboardPersonRow);
